import { Aide } from '@/components/ui/aide';
import React, { useEffect, useState } from 'react';

import { supplyApi } from '../../lib/supplyApi';
import { formatLitres, formatMontant, formatNombre } from '../../lib/formats';
import { formatDateTime } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';
import { BarrePagination } from '../ui/pagination-bar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { usePaginationServeur } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';

/**
 * Onglet « Pleins à la station » de la fiche camion (lot 035).
 *
 * Les pleins que le camion a faits à la pompe de nos stations (vente « Camion
 * de la société » : aucune recette). Le coût est celui du carburant dans la
 * cuve au moment du plein, réservé aux profils qui voient les montants. Les
 * litres aux 100 km se lisent de plein à plein quand le GPS a roulé entre les
 * deux ; sinon le tiret.
 *
 * Les pleins se lisent par pages (20, 50 ou 100) demandées au serveur; le
 * résumé du haut porte sur tous les pleins du camion, jamais sur la page.
 */
export default function TruckStationRefuelsTab({ truck }) {
  const { canSeeMoney } = useAuth();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [total, setTotal] = useState(0);
  const truckId = truck?.id;
  const pagination = usePaginationServeur(total, { cle: 'pleins', reinitialiser: truckId || '' });
  const { skip, limit } = pagination;

  useEffect(() => {
    if (!truckId) return undefined;
    let annule = false;
    setChargement(true);
    supplyApi
      .truckRefuels(truckId, { limit, skip })
      .then((reponse) => {
        if (annule) return;
        setDonnees(reponse);
        // Un serveur plus ancien ne donne pas le total: la liste tient alors en une page.
        setTotal(Number(reponse?.total ?? reponse?.refuels?.length ?? 0));
      })
      .catch(() => {
        if (annule) return;
        setDonnees({ refuels: [], summary: null });
        setTotal(0);
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, [truckId, skip, limit]);

  const pleins = donnees?.refuels || [];
  const resume = donnees?.summary;
  // Première lecture, ou page vide le temps de revenir à la dernière page qui existe.
  if (chargement && pleins.length === 0) return <LoadingState shape="table" />;
  if (pleins.length === 0 && total > 0) return <LoadingState shape="table" />;
  if (!pleins.length) {
    return <EmptyState variant="empty" title="Aucun plein à la station" data-testid="truck-refuels-empty" />;
  }

  return (
    <div className="space-y-3" data-testid="truck-station-refuels">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="text-muted-foreground">Pleins </span>
          <span className="font-semibold tabular-nums">{resume?.refuel_count ?? pleins.length}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Litres </span>
          <span className="font-semibold tabular-nums">{formatLitres(resume?.total_liters)}</span>
        </span>
        {canSeeMoney && resume?.total_cost !== null && resume?.total_cost !== undefined ? (
          <span>
            <span className="text-muted-foreground">Coût </span>
            <span className="font-semibold tabular-nums">{formatMontant(resume.total_cost)}</span>
          </span>
        ) : null}
        <span className="flex items-center gap-1">
          <span className="text-muted-foreground">Consommation </span>
          <span className="font-semibold tabular-nums">
            {resume?.l_per_100km === null || resume?.l_per_100km === undefined
              ? '—'
              : `${formatNombre(resume.l_per_100km, 1)} L/100 km`}
          </span>
          <Aide titre="Pleins à la station">
            Pleins faits à la pompe de nos stations, sans recette. Le coût est celui du carburant dans la cuve au moment
            du plein. La consommation se calcule de plein à plein avec la distance GPS ; sans GPS entre deux pleins, elle
            n’est pas affichée.
            {resume?.refuel_count > resume?.consumption_refuel_count
              ? ` Elle est calculée sur les ${resume.consumption_refuel_count} pleins les plus récents.`
              : ''}
          </Aide>
        </span>
      </div>

      <div className={cn(chargement && 'opacity-60 transition-opacity')} aria-busy={chargement}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Station</TableHead>
              <TableHead className="text-right">Litres</TableHead>
              <TableHead className="text-right">Distance</TableHead>
              <TableHead className="text-right">L/100 km</TableHead>
              {canSeeMoney ? <TableHead className="text-right">Coût</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pleins.map((plein) => (
              <TableRow key={plein.sale_id} data-testid="truck-refuel-row">
                <TableCell>{formatDateTime(plein.sale_date)}</TableCell>
                <TableCell>{plein.station_name || '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{formatLitres(plein.liters)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {plein.distance_km === null || plein.distance_km === undefined ? '—' : `${formatNombre(plein.distance_km, 0)} km`}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {plein.l_per_100km === null || plein.l_per_100km === undefined ? '—' : formatNombre(plein.l_per_100km, 1)}
                </TableCell>
                {canSeeMoney ? (
                  <TableCell className="text-right tabular-nums">
                    {plein.total_cost === null || plein.total_cost === undefined ? '—' : formatMontant(plein.total_cost)}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <BarrePagination pagination={pagination} nom="pleins" />
    </div>
  );
}
